import { createTheme, ThemeOptions, ThemeProvider } from "@mui/material";
import { PropsWithChildren, useMemo } from "react";
import { daDK as muiDaDK, enUS as muiEnUS } from "@mui/material/locale";
import { daDK, enUS } from "@mui/x-data-grid-pro/locales";
import { avenirNextLTProFonts } from "src/avenirNextLtProFonts";
import { useUserSettings } from "src/hooks";

export const CustomThemeProvider = (props: PropsWithChildren) => {
  const { children } = props;

  const { language } = useUserSettings();

  const baseTheme: ThemeOptions = {
    typography: {
      fontFamily: "AvenirNextLTPro, sans-serif",
      h1: { fontSize: "2rem", fontWeight: 700 },
      h2: { fontSize: "1.5rem", fontWeight: 700 },
      h3: { fontSize: "1.25rem", fontWeight: 700 },
      h4: { fontSize: "1rem", fontWeight: 700 },
      body1: { fontSize: "1rem", fontWeight: "normal" },
      body2: { fontSize: "1rem", fontWeight: 600 },
      subtitle1: { fontSize: "0.875rem", fontWeight: "normal" },
      subtitle2: { fontSize: "0.875rem", fontWeight: 600 },
      caption: { fontSize: "0.75rem", fontWeight: "normal" }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          body {
            font-family: AvenirNextLTPro, sans-serif;
            height: 100vh;
            margin: 0;
          }

          body *::-webkit-scrollbar-track {
            border-radius: 10px;
            background-color: transparent;
          }

          body *::-webkit-scrollbar {
            width: 10px;
            background-color: transparent;
          }

          body *::-webkit-scrollbar-thumb {
            border-radius: 10px;
            background-color: #B2B6C9;
          }

          #root {
            height: 100%;
          }

          @keyframes rowHighlight {
            0% {
              background-color: rgba(0, 88, 100, 0.15);
              box-shadow: inset 0 0 0 2px #005864;
            }

            100% {
              background-color: transparent;
              box-shadow: inset 0 0 0 0 transparent;
            }
          }

          .row-highlight {
            animation: rowHighlight 2s ease-out;
          }
          ${avenirNextLTProFonts}
        `
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            transform: "none"
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 4
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-root": {
              backgroundColor: "#fff"
            }
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: {
                  orientation: "horizontal"
                },
                style: {
                  "& .MuiTabs-list": {
                    borderBottom: "1.5px solid #B9BDCF",
                    gap: "24px"
                  },
                  "& .MuiTabs-indicator": {
                    height: "3px",
                    backgroundColor: "#005864"
                  },
                  "& .Mui-selected": {
                    fontWeight: 700
                  }
                }
              }
            ]
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minWidth: "auto",
            padding: "12px 0px",
            textTransform: "none",
            fontSize: "1rem"
          }
        }
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cellOffsetLeft": {
              border: "2px solid #005864",
              marginRight: "-3px"
            }
          }
        }
      }
    },
    palette: {
      primary: { main: "#006D7B" },
      secondary: { main: "#005864" },
      customColors: {
        sidebarBackground: "#e3e3e3",
        black: "#3D4051",
        darkGrey: "#575B71",
        grey: "#E0E0E0",
        grey2: "#B2B6C9",
        lightGrey: "#EDEEF2",
        lightGrey2: "#F9F9FA",
        white: "#FFFFFF",
        border: "#B9BDCF",
        whiteFaint: "rgba(255,255,255,0.10)",
        whiteSurface: "rgba(255,255,255,0.12)",
        whiteChip: "rgba(255,255,255,0.20)",
        whiteMuted: "rgba(255,255,255,0.30)",
        whiteBorder: "rgba(255,255,255,0.40)",
        whiteBorderStrong: "rgba(255,255,255,0.50)",
        whiteStrong: "rgba(255,255,255,0.70)",
        whiteHover: "rgba(255,255,255,0.85)",
        warningOnPrimary: "#FFC107",
        errorOnPrimary: "#FF5252",
        darkGreen: "#005864",
        green: "#006D7B"
      }
    }
  };

  const theme = useMemo(() => {
    let locale = enUS;
    let muiLocale = muiEnUS;
    switch (language) {
      case "en":
        locale = enUS;
        muiLocale = muiEnUS;
        break;
      case "da":
        locale = daDK;
        muiLocale = muiDaDK;
    }
    return createTheme(baseTheme, muiLocale, locale);
  }, [baseTheme, language]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
